import FormHeader from "@/components/booking/FormHeader.tsx";
import React, {useContext, useEffect, useImperativeHandle, useRef} from "react";
import {OscarContext, OscarContextType} from "@/components/context/OscarContext.tsx";
import {FormDescription} from "@/components/ui/form.tsx";
import amexLogo from "../../../assets/cards/amex.svg";
import visaLogo from "../../../assets/cards/visa.svg";
import mastercardLogo from "../../../assets/cards/mastercard.svg";
import squareLogo from "../../../assets/cards/square.svg";
import {Card, payments, TokenResult} from '@square/web-sdk';
import type { Payments } from '@square/web-sdk';
import * as Sentry from "@sentry/react";

export type SquareDivElement = HTMLDivElement & {
    error: string|null;
    loading: boolean;
    payments: Payments|null;
    card: Card|null;
};

export type PaymentsFormRef = {
    getCardDetails: () => Promise<TokenResult|null>;
}

type Props = object;

export const PaymentsForm = React.forwardRef<PaymentsFormRef, Props>((_props, ref) => {
    const { appConfig } = useContext(OscarContext) as OscarContextType
    const squareRef = useRef<SquareDivElement>(null);

    useEffect(  () => {
        const element = squareRef.current;
        if (!element) {
            return;
        }

        // Already loaded or loading.
        if (element.error || element.loading !== undefined || element.payments || element.card) {
            return;
        }

        if (!appConfig?.configData?.sqAppId || !appConfig?.configData?.sqLocationId) {
            element.error = "Something went wrong while loading Square payments, please try again later or call to place your booking.";
            return;
        }

        async function setupPayments(appId: string, locationId: string) {
            if (!element) {
                return;
            }
            try {
                element.payments = await payments(appId, locationId);
                if (!element.payments) {
                    element.loading = false;
                    element.error = "Something went wrong while loading Square payments, please try again later or call to place your booking.";
                    return;
                }
                element.card = await element.payments.card({});
                await element.card.attach(squareRef.current!);
                element.loading = false;
            } catch (e) {
                Sentry.captureException(e);
                element.loading = false;
                element.error = "Something went wrong while loading Square payments, please try again later or call to place your booking.";
            }
        }

        element.loading = true;
        setupPayments(appConfig.configData.sqAppId, appConfig.configData.sqLocationId);
    }, [appConfig?.configData?.sqAppId, appConfig?.configData?.sqLocationId]);

    /**
     * Get the card details from the Square card element.
     */
    const getCardDetails = async () => {
        const element = squareRef.current;
        if (!element || !element.card) {
            return null;
        }

        return await element.card.tokenize();
    };

    /**
     * Expose the getCardDetails function to the parent component.
     */
    useImperativeHandle(ref, () => ({
        getCardDetails
    }));

    return (
        <div>
            <div className={'os-flex os-flex-col md:os-flex-row os-gap-2 os-items-start os-mb-4'}>
                <div className={'os-flex-1'}>
                    <FormHeader>Payment Details</FormHeader>
                    <FormDescription>We accept Visa, Mastercard & American Express. Your payment will be securely
                        processed using Square.</FormDescription>
                </div>
                <div className={'os-flex os-flex-row os-gap-2'}>
                    <a href={"https://squareup.com/"} target={"_blank"} rel={"noreferrer"} title={"Open Square"}>
                        <img src={squareLogo} className={"os-w-12"} alt={"Square Logo"}/>
                    </a>
                    <img src={visaLogo} className={"os-w-12"} alt={"Visa Logo"}/>
                    <img src={mastercardLogo} className={"os-w-12"} alt={"Mastercard Logo"}/>
                    <img src={amexLogo} className={"os-w-12"} alt={"American Express Logo"}/>
                </div>
            </div>
            <div ref={squareRef}></div>
        </div>
    );
});
